import * as THREE from 'three';
import { ArrowEntity } from './ArrowEntity';
import { ArrowType } from './ArrowType';
import { Enemy } from '../enemies/Enemy';

/**
 * Manages a pool of arrow entities for efficient reuse
 */
export class ArrowPool {
  // Scene reference
  private scene: THREE.Scene;
  
  // Active arrows
  private activeArrows: ArrowEntity[] = [];
  
  // Inactive arrows (pool)
  private inactiveArrows: Map<ArrowType, ArrowEntity[]> = new Map();
  
  // Maximum arrows per type
  private maxArrowsPerType: number = 10;
  
  /**
   * Creates a new ArrowPool
   * @param scene The scene to add arrows to
   */
  constructor(scene: THREE.Scene) {
    this.scene = scene;
    
    // Initialize arrow pools for each type
    this.inactiveArrows.set(ArrowType.NORMAL, []);
    this.inactiveArrows.set(ArrowType.FIRE, []);
    this.inactiveArrows.set(ArrowType.EXPLOSIVE, []);
    this.inactiveArrows.set(ArrowType.POISON, []);
    
    console.log("ArrowPool created");
  }
  
  /**
   * Gets an arrow of the specified type
   * @param type The arrow type
   * @returns An arrow entity
   */
  public getArrow(type: ArrowType): ArrowEntity {
    // Get pool for this arrow type
    const pool = this.inactiveArrows.get(type) || [];
    
    // Try to reuse an inactive arrow
    if (pool.length > 0) {
      const arrow = pool.pop()!;
      
      // Make sure the arrow is properly reset before reuse
      arrow.deactivate(); // This will clear hitEnemies
      
      this.activeArrows.push(arrow);
      return arrow;
    }
    
    // Create a new arrow if pool is empty
    const arrow = new ArrowEntity(type);
    this.scene.add(arrow.getObject());
    this.activeArrows.push(arrow);
    return arrow;
  }
  
  /**
   * Updates all active arrows
   * @param deltaTime Time since last update in seconds
   * @param enemies Optional array of enemies to check for collisions
   */
  public update(deltaTime: number, enemies?: Enemy[]): void {
    // Update active arrows and remove inactive ones
    for (let i = this.activeArrows.length - 1; i >= 0; i--) {
      const arrow = this.activeArrows[i];
      
      // Update arrow physics
      const isActive = arrow.update(deltaTime);
      
      // Check for enemy collisions if enemies are provided
      if (isActive && enemies && enemies.length > 0) {
        arrow.checkEnemyCollisions(enemies);
      }
      
      // If arrow is no longer active, move it back to the pool
      if (!isActive) {
        // Remove from active arrows
        this.activeArrows.splice(i, 1);
        
        // Get pool for this arrow type
        const type = arrow.getType();
        const pool = this.inactiveArrows.get(type) || [];
        
        // Add to pool if not full
        if (pool.length < this.maxArrowsPerType) {
          pool.push(arrow);
        } else {
          // Remove from scene and dispose if pool is full
          this.scene.remove(arrow.getObject());
          arrow.dispose();
        }
      }
    }
  }
  
  /**
   * Gets the number of active arrows
   * @returns The number of active arrows
   */
  public getActiveArrowCount(): number {
    return this.activeArrows.length;
  }
  
  /**
   * Cleans up resources when the arrow pool is no longer needed
   */
  public dispose(): void {
    // Dispose of all active arrows
    for (const arrow of this.activeArrows) {
      this.scene.remove(arrow.getObject());
      arrow.dispose();
    }
    
    // Dispose of all inactive arrows
    for (const [_, pool] of this.inactiveArrows) {
      for (const arrow of pool) {
        this.scene.remove(arrow.getObject());
        arrow.dispose();
      }
    }
    
    // Clear arrays
    this.activeArrows = [];
    this.inactiveArrows.clear();
  }
} 